import Phaser from 'phaser';
import { ShapeBody } from './shapeFactory';

/**
 * Squash-and-stretch on hard landings, for tactile weight when a shape hits
 * the floor or the pile.
 *
 * Why a ghost sprite instead of tweening the body's scale: a Matter.Image's
 * setScale mutates the underlying physics body's vertices (see mergeManager's
 * punchScale note), which races against merges/destroys. So we spawn a plain
 * Image copy, hide the real body for the ~200 ms squash, deform the ghost,
 * then reveal the body and drop the ghost. The collider is never touched.
 */
export class ImpactFx {
  private scene: Phaser.Scene;
  // Per-shape cooldown so a piece settling/jostling doesn't squash every frame.
  private lastSquashAt = new Map<number, number>();

  // Minimum impact speed (px/step) to bother squashing, and the speed at which
  // the squash maxes out. Tuned against the game's drop velocities.
  private static readonly MIN_SPEED = 5;
  private static readonly MAX_SPEED = 18;
  private static readonly COOLDOWN_MS = 230;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    scene.matter.world.on('collisionstart', this.onCollision, this);
  }

  destroy(): void {
    this.scene.matter.world.off('collisionstart', this.onCollision, this);
    this.lastSquashAt.clear();
  }

  private onCollision(event: Phaser.Physics.Matter.Events.CollisionStartEvent): void {
    const now = this.scene.time.now;
    for (const pair of event.pairs) {
      const a = (pair.bodyA as MatterJS.BodyType).gameObject as ShapeBody | undefined;
      const b = (pair.bodyB as MatterJS.BodyType).gameObject as ShapeBody | undefined;
      // Only squash the faster-moving participant so a drop onto a settled
      // pile flattens the dropped piece, not the whole stack.
      const candidates = [a, b].filter(
        (go): go is ShapeBody => !!go && go.active && !go.merging && typeof go.shapeTier === 'number',
      );
      if (candidates.length === 0) continue;
      candidates.sort((x, y) => this.speedOf(y) - this.speedOf(x));
      this.trySquash(candidates[0], now);
    }
  }

  private speedOf(go: ShapeBody): number {
    const v = (go.body as MatterJS.BodyType).velocity;
    return Math.hypot(v.x, v.y);
  }

  private trySquash(go: ShapeBody, now: number): void {
    const speed = this.speedOf(go);
    if (speed < ImpactFx.MIN_SPEED) return;
    const last = this.lastSquashAt.get(go.shapeId) ?? -Infinity;
    if (now - last < ImpactFx.COOLDOWN_MS) return;
    this.lastSquashAt.set(go.shapeId, now);

    const key = go.texture?.key;
    if (!key) return;

    const amt = Phaser.Math.Clamp(
      (speed - ImpactFx.MIN_SPEED) / (ImpactFx.MAX_SPEED - ImpactFx.MIN_SPEED),
      0,
      1,
    );
    // Flatten on impact: wider in X, shorter in Y. Capped so it stays playful,
    // not rubbery.
    const squash = 0.08 + amt * 0.2;

    // Squash relative to the live sprite's scale — tier scale is 1.32 on
    // tablet/desktop, so a unit-scale ghost would visibly pop the shape
    // smaller for the duration of the squash.
    const sx = go.scaleX;
    const sy = go.scaleY;
    const ghost = this.scene.add
      .image(go.x, go.y, key)
      .setDepth(Math.max(14, go.depth))
      .setRotation(go.rotation)
      .setOrigin(go.originX, go.originY)
      .setScale(sx * (1 + squash), sy * (1 - squash));
    go.setVisible(false);

    const follow = () => {
      if (!ghost.active || !go.active) return;
      ghost.setPosition(go.x, go.y);
      ghost.setRotation(go.rotation);
    };
    this.scene.events.on('update', follow);

    const cleanup = () => {
      this.scene.events.off('update', follow);
      if (go.active) go.setVisible(true);
      if (ghost.active) ghost.destroy();
    };
    // If the shape merges/destroys mid-squash, bail immediately — the merge's
    // own pop effect takes over from there.
    go.once('destroy', () => {
      this.scene.events.off('update', follow);
      if (ghost.active) ghost.destroy();
    });

    this.scene.tweens.add({
      targets: ghost,
      scaleX: sx,
      scaleY: sy,
      duration: 200,
      ease: 'Back.easeOut',
      onComplete: cleanup,
    });
  }
}
